import {
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { RefreshCw } from 'lucide-react';
import React from 'react';
import type { ExternalAgentBinding } from '../../types';
import { useTranslation } from 'react-i18next';

interface BindingsTableProps {
  bindings: ExternalAgentBinding[];
  loading: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  onStatusChange: (binding: ExternalAgentBinding, status: string) => void;
}

export const BindingsTable: React.FC<BindingsTableProps> = ({
  bindings,
  loading,
  canRefresh,
  onRefresh,
  onStatusChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-800 dark:text-slate-200">
          {t('apiKeys.integrationClients.bindings')}
        </div>
        <Button
          type="button"
          size="small"
          variant="text"
          startIcon={<RefreshCw size={14} />}
          disabled={!canRefresh || loading}
          onClick={onRefresh}
        >
          {t('apiKeys.integrationClients.refresh')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          <CircularProgress size={18} />
          {t('apiKeys.integrationClients.loadingBindings')}
        </div>
      ) : bindings.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-slate-600 dark:text-slate-400">
          {t('apiKeys.integrationClients.noBindings')}
        </div>
      ) : (
        <TableContainer className="max-h-[360px] overflow-auto rounded-lg border border-gray-200 dark:border-slate-700">
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 720,
              '& .MuiTableCell-root': { color: 'inherit' },
              '& .MuiTableCell-head': {
                backgroundColor: 'rgb(249 250 251)',
                color: 'rgb(107 114 128)',
                fontSize: 12,
                fontWeight: 700,
              },
              '.dark & .MuiTableCell-head': {
                backgroundColor: 'rgb(15 23 42)',
                color: 'rgb(148 163 184)',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('apiKeys.integrationClients.tenant')}</TableCell>
                <TableCell>{t('apiKeys.integrationClients.agent')}</TableCell>
                <TableCell>{t('apiKeys.integrationClients.usage')}</TableCell>
                <TableCell>{t('apiKeys.integrationClients.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bindings.map((binding) => (
                <TableRow key={binding.public_id} hover>
                  <TableCell>
                    <div className="min-w-[180px]">
                      <div className="truncate text-xs font-semibold text-gray-800 dark:text-slate-200">
                        {binding.external_tenant_id}
                      </div>
                      <div className="truncate text-xs text-gray-400 dark:text-slate-500">
                        {binding.external_tenant_type || 'tenant'}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[220px]">
                      <div className="truncate text-xs text-gray-700 dark:text-slate-300">
                        {binding.agent_name}
                      </div>
                      <div className="truncate font-mono text-xs text-gray-400 dark:text-slate-500">
                        {binding.agent_public_id}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[110px] text-xs text-gray-600 dark:text-slate-300">
                      {(binding.usage?.messages ?? 0).toLocaleString()} msg
                      <br />
                      {(binding.usage?.total_tokens ?? 0).toLocaleString()} tok
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex min-w-[150px] items-center gap-2">
                      <Chip
                        size="small"
                        label={binding.status}
                        color={binding.status === 'active' ? 'success' : 'default'}
                      />
                      {binding.status === 'active' ? (
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          onClick={() => onStatusChange(binding, 'disabled')}
                        >
                          {t('apiKeys.integrationClients.disable')}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          onClick={() => onStatusChange(binding, 'active')}
                        >
                          {t('apiKeys.integrationClients.enable')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
